import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { PublisherViewStore } from "../../store";
import { PublisherView } from "../view/PublisherView";
import { PublisherUpdateStore } from "../../store";
import { PublisherUpdate } from "../update/PublisherUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type PublisherModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const PublisherModalContent: React.FC<PublisherModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new PublisherUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new PublisherViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <PublisherView
            publisherViewStore={storeInfo.store as PublisherViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <PublisherUpdate
            publisherUpdateStore={storeInfo.store as PublisherUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
